import {useEffect, useRef, useState} from 'react';

import {productInsertT, productT} from '@/lib/types/product.types';

import {productCategories, productCategoryT} from '@/lib/constants';
import {getCreateProductFields} from '@/lib/helpers';
import {ProductInstance} from '@/lib/utils';

import {initialProductFormData} from '@/pages/admins/products/adminProducts';

import {useDataQuery} from '@/hooks/useDataQuery';
import {useItemQuery} from '@/hooks/useItemQuery';
import {useViewQuery} from '@/hooks/useViewQuery';

/**
 * useProductData Hook - Handles the product data for the product data pages.
 * pages: brand detail for products and adminProductsPage
 * @param {number} brandId - The brand id
 * @returns {object}
 */
export function useProductData(brandId?: number) {
  // State
  const [formData, setFormData] = useState<productInsertT>(initialProductFormData);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [subCategoryOptions, setSubCategoryOptions] = useState<{label: string; value: string}[]>([]);

  // Refs
  const isEditingRef = useRef(false);

  // Tanstack Queries
  const {data: {data} = {data: []}, isLoading} = useDataQuery({
    table: 'Product',
  });

  const {data: product, error: productError} = useItemQuery({
    table: 'Product',
    id: selectedProductId!,
    enabled: !!selectedProductId,
    columns: ['*', ['Brand', ['*']], ['Weedmap_Product', ['*']]],
    staleTime: 0, // Get the latest data
  });

  const {data: stats} = useViewQuery({
    view: 'product_stats',
  });

  /**
   * Adjust the product categories to be used in the form select input.
   */
  const adjustedCategories = Object.keys(productCategories).map(category => ({
    label: category,
    value: category,
  }));

  /**
   * Set the sub category options based on the selected category.
   * If no category is selected, default to the first category.
   * @param formData.product_category - The selected category.
   * @returns void
   * */
  useEffect(() => {
    const selectedCategory = formData.product_category as productCategoryT;
    if (selectedCategory) {
      const subCategories = productCategories[selectedCategory] || [];
      const formattedSubCategories = subCategories.map(subCategory => ({
        label: subCategory,
        value: subCategory,
      }));

      setSubCategoryOptions(formattedSubCategories);
    } else {
      const firstCategory = Object.keys(productCategories)[0] as productCategoryT;
      const subCategories = productCategories[firstCategory] || [];
      const formattedSubCategories = subCategories.map(subCategory => ({
        label: subCategory,
        value: subCategory,
      }));

      setSubCategoryOptions(formattedSubCategories);
    }
  }, [formData.product_category]);

  /**
   * Create the form fields for the create product form.
   * @returns formDataT[]
   * */
  const createFields = getCreateProductFields(adjustedCategories, subCategoryOptions);

  /**
   * Handle the add product button click.
   * Resets the form data and sets the modal to visible.
   * @returns void
   * */
  const handleAddProductClick = () => {
    isEditingRef.current = false;
    setFormData(initialProductFormData);
    // setIsVisible(true);
  };

  /**
   * Handle the edit product button click.
   * Sets the form data to the selected product and sets the modal to visible.
   * @param {productT} product - The selected product.
   * @returns void
   * */
  const handleEditProductClick = (id: number) => {
    isEditingRef.current = true;
    setSelectedProductId(id);
    setIsVisible(true);
  };

  /**
   * Handle the delete product button click.
   * Sets the selected product id and sets the delete modal to visible.
   * @param {productT} product - The selected product.
   * @returns void
   * */
  const handleDeleteProductClick = (id: number) => {
    setSelectedProductId(id);
    setIsDeleteModalVisible(true);
  };

  /**
   * Handle the product form submission.
   * If editing, update the product. If not, create a new product.
   * @param {productInsertT} data - The product data to submit.
   * @returns void
   * */
  const handleSubmitProduct = async (data: productInsertT) => {
    setIsSubmitting(true);
    const productData: productInsertT = {
      ...data,
      fk_brand_id: brandId ?? data.fk_brand_id,
    };

    try {
      await ProductInstance.createProduct(productData);
      setIsVisible(false);
      setIsSubmitting(false);
    } catch (error) {
      setSubmissionError((error as Error).message);
      setIsSubmitting(false);
    }
  };

  /**
   * Handle the product update form submission.
   * Updates the product with the new data.
   * @param {productT} data - The updated product data.
   * @returns void
   * */
  const handleUpdateProduct = async (data: productT) => {
    if (!selectedProductId) {
      console.error('No product id found');
      return;
    }

    setIsSubmitting(true);

    const updatedProduct = {
      ...data,
      product_id: selectedProductId,
    };

    try {
      await ProductInstance.updateProduct(updatedProduct);
      setIsVisible(false);
      setIsSubmitting(false);
      //loadProducts();
    } catch (error) {
      setSubmissionError((error as Error).message);
      setIsSubmitting(false);
    }
  };

  /**
   * Handle the product delete form submission.
   * Deletes the selected product.
   * @returns void
   * */
  const handleDeleteProduct = async () => {
    if (!selectedProductId) {
      console.error('No product id found');
      return;
    }
    try {
      await ProductInstance.deleteProduct(selectedProductId);
      setIsDeleteModalVisible(false);
    } catch (error) {
      setSubmissionError((error as Error).message);
    }
  };

  /**
   * Handle the product delete form submission.
   * Deletes the selected product.
   * @returns void
   * */
  const handleCancelDeleteProduct = async () => {
    setSelectedProductId(null);
    setIsDeleteModalVisible(false);
  };

  /**
   * Handle the product delete form submission.
   * Deletes the selected product.
   * @returns void
   * */
  const handleCloseForm = async () => {
    setSelectedProductId(null);
    setIsVisible(false);
  };

  return {
    data,
    formData,
    setFormData,
    isVisible,
    isEditing: isEditingRef.current,
    setIsVisible,
    isSubmitting,
    submissionError,
    setSubmissionError,
    createFields,
    isDeleteModalVisible,
    setIsDeleteModalVisible,
    handleAddProductClick,
    handleEditProductClick,
    handleDeleteProductClick,
    handleSubmitProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleCancelDeleteProduct,
    handleCloseForm,
    isLoading,
    product,
    productError,
    stats,
  };
}
